import { useState, useMemo } from 'react';
import { DEVOPS_ICONS, ICON_CATEGORIES, searchIcons } from '@/lib/icons/devops-icons-cdn';
import useDrawStore, { ELEMENT_TYPES } from '@/stores/drawStore';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Search, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function IconLibrary({ onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [draggedIcon, setDraggedIcon] = useState(null);
  
  const { addElement, toggleIconLibrary } = useDrawStore();

  // Filter icons based on search and category
  const filteredIcons = useMemo(() => {
    let icons = searchQuery ? searchIcons(searchQuery) : DEVOPS_ICONS;
    
    if (activeCategory !== 'all') {
      icons = icons.filter(icon => icon.category === activeCategory);
    }
    
    return icons;
  }, [searchQuery, activeCategory]);

  // Group icons by category for display
  const iconsByCategory = useMemo(() => {
    const grouped = {};
    ICON_CATEGORIES.forEach(cat => {
      grouped[cat.id] = filteredIcons.filter(icon => icon.category === cat.id);
    });
    return grouped;
  }, [filteredIcons]);

  // Handle icon drag start
  const handleDragStart = (e, icon) => {
    setDraggedIcon(icon);
    e.dataTransfer.setData('application/json', JSON.stringify(icon));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle icon click (add to canvas)
  const handleIconClick = (icon) => {
    addElement(ELEMENT_TYPES.ICON, {
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: 64,
      height: 64,
      iconUrl: icon.iconUrl,
      iconId: icon.id,
      iconName: icon.name,
    });
  };

  // Render single icon with real image
  const renderIcon = (icon) => (
    <TooltipProvider key={icon.id} delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "relative p-2 rounded-lg border border-transparent cursor-pointer transition-all",
              "hover:border-primary/50 hover:bg-primary/5",
              "active:scale-95"
            )}
            draggable
            onDragStart={(e) => handleDragStart(e, icon)}
            onClick={() => handleIconClick(icon)}
            data-testid={`icon-${icon.id}`}
          >
            <img
              src={icon.iconUrl}
              alt={icon.name}
              className="w-12 h-12 mx-auto object-contain"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <p className="text-[10px] text-center mt-1 truncate text-muted-foreground">
              {icon.name}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="font-medium">{icon.name}</p>
          <p className="text-xs text-muted-foreground capitalize">{icon.category}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {icon.keywords.slice(0, 3).map((kw, i) => (
              <Badge key={i} variant="secondary" className="text-[10px] px-1 py-0">
                {kw}
              </Badge>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="flex flex-col h-full bg-background border-l border-border w-[320px]">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Icônes DevOps</h3>
          <Badge variant="secondary" className="text-xs">
            {DEVOPS_ICONS.length}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => toggleIconLibrary()}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Search */}
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher (ex: kubernetes, ci/cd...)"
            className="pl-9 pr-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="icon-search"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        {searchQuery && (
          <p className="text-xs text-muted-foreground mt-2">
            {filteredIcons.length} résultat(s) pour "{searchQuery}"
          </p>
        )}
      </div>

      {/* Category Tabs */}
      <Tabs
        value={activeCategory}
        onValueChange={setActiveCategory}
        className="flex-1 flex flex-col overflow-hidden"
      >
        <div className="border-b border-border overflow-x-auto">
          <TabsList className="inline-flex h-auto p-1 bg-transparent gap-1">
            <TabsTrigger
              value="all"
              className="text-xs px-2 py-1 rounded data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              Tous
            </TabsTrigger>
            {ICON_CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className="text-xs px-2 py-1 rounded whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {cat.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <ScrollArea className="flex-1">
          <TabsContent value="all" className="mt-0 p-3">
            {ICON_CATEGORIES.map((category) => {
              const categoryIcons = iconsByCategory[category.id];
              if (categoryIcons.length === 0) return null;

              return (
                <div key={category.id} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <h4 className="text-sm font-medium">{category.name}</h4>
                    <Badge variant="outline" className="text-[10px]">
                      {categoryIcons.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    {categoryIcons.map(renderIcon)}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {ICON_CATEGORIES.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0 p-3">
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <h4 className="font-medium">{category.name}</h4>
                <Badge variant="outline">
                  {iconsByCategory[category.id]?.length || 0}
                </Badge>
              </div>
              
              {iconsByCategory[category.id]?.length > 0 ? (
                <div className="grid grid-cols-4 gap-1">
                  {iconsByCategory[category.id].map(renderIcon)}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Aucune icône trouvée</p>
                </div>
              )}
            </TabsContent>
          ))}
        </ScrollArea>
      </Tabs>

      {/* Footer */}
      <div className="p-3 border-t border-border bg-muted/30">
        <p className="text-xs text-muted-foreground text-center">
          Glissez-déposez ou cliquez pour ajouter une icône
        </p>
      </div>
    </div>
  );
}
